import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import * as CreditsActions from '../../../../actions/credits'
import { useAppDispatch } from '../../../../actions/types'
import { colors, Routes } from '../../../../constants'
import {
    Button,
    Column,
    Confirmation,
    Form,
    Headings,
    Input,
    Padding,
} from '../../../../components'
import { Label } from '../../../../components/Form'
import type { Credit } from '../../../../types'
import type { CreditFormData } from '../types'
import useCreditParams from '../useCreditParams'
import * as Validation from '../validation'
import ErrorMessages from './ErrorMessages'
import useCreditFormData from './useCreditFormData'
import useDisplayTypes from './useDisplayTypes'
import { FullWidthLabel, NameSection } from '../../Promos/PromoForm/PromoForm.styles'

export default function CreditForm({ credit }: { credit?: Credit }) {
    const dispatch = useAppDispatch()
    const creditTypes = useDisplayTypes()
    const history = useHistory()
    const { t } = useTranslation()
    const { estate } = useCreditParams()
    const {
        areUnsavedChanges,
        formErrors,
        onSubmit,
        creditData,
        saveProgress,
        setCreditData,
        setUnsavedChanges,
        submitError,
    } = useCreditFormData(credit)

    const {
        id: credit_id,
        start_date,
        end_date,
        skus,
        type,
    } = creditData

    React.useEffect(() => {
        dispatch(CreditsActions.fetchCreditTypes())
    }, [dispatch, estate])

    const buildOnChange = (attr: Exclude<keyof CreditFormData, 'start_date' | 'end_date'>) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            setUnsavedChanges(true)

            let value: string | string[] = event.target.value

            if (attr === 'skus') {
                value = (value as string).replace(/(?<=\w)(\s)$/, ',')
                    .split(',')
                    .map(sku => sku.replace(/[^\w]/g, ''))
            }

            setCreditData(prevData => {
                return {
                    ...prevData,
                    [attr]: value,
                }
            })
        }
    }

    const onChangeDates = (attr: 'end_date' | 'start_date') => {
        return (value: number) => {
            setUnsavedChanges(true)
            setCreditData(prevData => {
                return {
                    ...prevData,
                    [attr]: new Date(value),
                }
            })
        }
    }

    const onDeleteCredit = () => {
        if (!credit_id) {
            return
        }

        const fn = async () => {
            await dispatch(CreditsActions.deleteCredit(credit_id))

            history.replace(
                Routes.buildRoute(Routes.SELF_SERVE_CREDITS, { estate })
            )
        }

        fn()
    }

    const isNew = !credit

    const thereAreErrors =
        (validation: Validation.CreditValidation) => Object.keys(validation).length > 0

    const firstError = thereAreErrors(formErrors)
        ? formErrors[Object.keys(formErrors)[0] as keyof Validation.CreditValidation]
        : undefined

    const SkusLabel = skus.length === 0
        ? t('credit-skus')
        : `${t('credit-skus')} (${skus.length})`

    return (
        <Padding>
            <Form>
                <NameSection top bottom>
                    <Column width="40%">
                        <Headings.H2>
                            {isNew ? t('credit-create') : t('credit-title')}
                        </Headings.H2>
                    </Column>
                    <Column width="60%" right>
                        <ErrorMessages
                            saveProgress={saveProgress}
                            areUnsavedChanges={areUnsavedChanges}
                            errorMessage={firstError ?? submitError?.message}
                        />
                        <Button
                            theme="forestGreen"
                            onClick={onSubmit}
                            disabled={!areUnsavedChanges}
                        >
                            {t('credit-save')}
                        </Button>

                        <Confirmation
                            cancel={t('cancel')}
                            confirmation={t('credit-remove-confirm-body')}
                            header={t('credit-remove-confirm-header')}
                            onCancel={() => null}
                            onConfirm={onDeleteCredit}
                            ok={t('credit-remove-confirm-button')}
                            theme="danger"
                        >
                            <Button
                                theme="danger"
                                disabled={isNew}
                            >
                                {t('credit-delete')}
                            </Button>
                        </Confirmation>
                    </Column>
                </NameSection>
                <Padding color={colors.white}>
                    <Column width="60%">
                        {!isNew && (
                            <Label id="credit-id" label={t('credit-id')} error={null}>
                                <Input
                                    id="credit-id"
                                    name="credit-id"
                                    type="text"
                                    size="sm"
                                    value={credit_id}
                                    disabled
                                />
                            </Label>
                        )}
                        <Label id="credit-type" label={t('credit-type')} error={null}>
                            <Input
                                id="credit-type"
                                name="credit-type"
                                type="select"
                                error={formErrors.type && t(formErrors.type)}
                                onChange={buildOnChange('type')}
                                size="sm"
                                value={type}
                                values={creditTypes}
                            />
                        </Label>
                        <Label id="credit-start-date" label={t('credit-start-date')} error={null}>
                            <Input
                                id="credit-start-date"
                                name="credit-start-date"
                                type="date"
                                error={formErrors.start_date && t(formErrors.start_date)}
                                onChange={onChangeDates('start_date')}
                                size="sm"
                                value={start_date}
                            />
                        </Label>
                        <Label id="credit-end-date" label={t('credit-end-date')} error={null}>
                            <Input
                                id="credit-end-date"
                                name="credit-end-date"
                                type="date"
                                error={formErrors.end_date && t(formErrors.end_date)}
                                onChange={onChangeDates('end_date')}
                                size="sm"
                                value={end_date}
                            />
                        </Label>
                    </Column>
                    <Column width="40%" left>
                        <FullWidthLabel id="credit-skus" label={SkusLabel} error={null}>
                            <Input
                                id="credit-skus"
                                name="credit-skus"
                                type="textarea"
                                error={formErrors.skus && t(formErrors.skus)}
                                onChange={buildOnChange('skus')}
                                size="lg"
                                value={skus.join(', ')}
                                style={{ height: '85px', width: '100%' }}
                                placeholder="654961, 5216854, 843159..."
                            />
                        </FullWidthLabel>
                    </Column>
                </Padding>
            </Form>
        </Padding>
    )
}
